import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';
import { toValue, type MaybeRefOrGetter } from 'vue';
import { apiClient } from '@/api/client';
import type { Notice, NoticeListQueryParams, PaginatedResponse } from './types';

// ---------------------------------------------------------------------------
// Error type
// ---------------------------------------------------------------------------

export interface ApiErrorBody {
  detail?: string | { msg?: string; code?: string }[];
  code?: string;
}

export type ApiError = AxiosError<ApiErrorBody>;

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const noticeKeys = {
  all: ['notices'] as const,
  list: (params: NoticeListQueryParams) => [...noticeKeys.all, 'list', params] as const,
  mine: () => [...noticeKeys.all, 'mine'] as const,
};

// ---------------------------------------------------------------------------
// Raw calls
// ---------------------------------------------------------------------------

async function getMyNotices(
  params: NoticeListQueryParams = {},
): Promise<PaginatedResponse<Notice>> {
  const query: Record<string, string | number | boolean> = {};
  if (params.page !== undefined) query.page = params.page;
  if (params.page_size !== undefined) query.page_size = params.page_size;
  if (params.unread_only === true) query.is_read = false;
  const { data } = await apiClient.get<PaginatedResponse<Notice>>('/notices/', {
    params: query,
  });
  return data;
}

async function patchMarkNoticeRead(noticeId: number): Promise<Notice> {
  const { data } = await apiClient.patch<Notice>(`/notices/${noticeId}/read`);
  return data;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useMyNoticesQuery(params: MaybeRefOrGetter<NoticeListQueryParams> = () => ({})) {
  return useQuery<PaginatedResponse<Notice>, ApiError>({
    queryKey: [...noticeKeys.mine()] as const,
    queryFn: () => getMyNotices(toValue(params)),
    // Refresh every 60 seconds for bell/badge.
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useMarkNoticeReadMutation() {
  const qc = useQueryClient();
  return useMutation<Notice, ApiError, number>({
    mutationFn: patchMarkNoticeRead,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: noticeKeys.all });
    },
  });
}
